/**
 * Tiny set of 14×14 stroked SVG icons used in the folder/tree row
 * menus on the Templates and Reports pages. Lucide-styled (1.5px
 * stroke, rounded line caps) so they sit naturally with the rest of
 * the SPA without dragging in lucide-react as a dependency.
 *
 * Add a new name here and both trees can use it; both menu types share
 * the same vocabulary intentionally.
 */
import type { JSX } from 'react';

export type MenuIconName =
  | 'open'
  | 'folder-plus'
  | 'rename'
  | 'move'
  | 'clone'
  | 'delete'
  | 'play'
  | 'people';

export function MenuIcon({ name }: { name: MenuIconName }): JSX.Element {
  const common = {
    width: 14,
    height: 14,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.75,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
  };
  switch (name) {
    case 'open':
      // External-link / open-in-place
      return (
        <svg {...common}>
          <path d="M15 3h6v6" />
          <path d="M10 14 21 3" />
          <path d="M21 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5" />
        </svg>
      );
    case 'folder-plus':
      return (
        <svg {...common}>
          <path d="M4 5h5l2 2h9a1 1 0 0 1 1 1v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2Z" />
          <path d="M12 11v6" />
          <path d="M9 14h6" />
        </svg>
      );
    case 'rename':
      // Pencil
      return (
        <svg {...common}>
          <path d="M12 20h9" />
          <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5Z" />
        </svg>
      );
    case 'move':
      // Folder with arrow
      return (
        <svg {...common}>
          <path d="M4 5h5l2 2h9a1 1 0 0 1 1 1v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2Z" />
          <path d="M9 14h6" />
          <path d="m13 11 3 3-3 3" />
        </svg>
      );
    case 'clone':
      // Two stacked rectangles (copy)
      return (
        <svg {...common}>
          <rect x="9" y="9" width="11" height="11" rx="2" />
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
        </svg>
      );
    case 'delete':
      // Trash
      return (
        <svg {...common}>
          <path d="M3 6h18" />
          <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
          <path d="M10 11v6" />
          <path d="M14 11v6" />
        </svg>
      );
    case 'people':
      // Two people (who can see it)
      return (
        <svg {...common}>
          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      );
    case 'play':
      return (
        <svg {...common}>
          <path d="M5 3.75v16.5L20.5 12 5 3.75Z" fill="currentColor" stroke="currentColor" />
        </svg>
      );
    default:
      return <svg {...common} />;
  }
}
